/**
 * Funzioni pure sull'elenco dei messaggi della chat associati.
 */

import type {
  ChatAllegato,
  ChatMenzione,
  ChatMessaggio,
  ChatRicevute,
  ChatRispostaA,
  EmojiReazioneRapida,
  StatoRicevuta,
} from '../../../shared/types/chat';
import { etichettaGiorno } from './chatDate';
import { eSticker } from './chatSticker';
import type { StickerMessaggio } from './chatSticker';

/** Le 6 reazioni rapide della riga veloce (si reagisce con qualsiasi emoji). Ridefinita qui: `shared/types/chat.ts` contiene solo tipi. */
export const EMOJI_REAZIONI: readonly EmojiReazioneRapida[] = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/** L'emoji è tra le 6 rapide. */
export const reazioneRapida = (emoji: string): boolean => (EMOJI_REAZIONI as readonly string[]).includes(emoji);

/** Messaggio mio in invio (o non riuscito), non ancora confermato dal server. */
export interface MessaggioLocale {
  clientId: string;
  stato: 'invio' | 'errore';
  testo: string;
  allegato?: ChatAllegato | null;
  rispostaA?: ChatRispostaA | null;
  creatoIl: string;
  conversazioneId: string;
  /** Motivo del mancato invio (solo con `stato: 'errore'`). */
  errore?: string;
  /** Foto appena scelta: URL `blob:` del browser, mostrato finché il server non conferma (lo revoca useConversazione). */
  anteprimaLocale?: string | null;
  /** Foto di un album in invio (stesso `album` per tutte, `albumIndice` 0-9). */
  album?: string | null;
  albumIndice?: number | null;
  /**
   * Menzioni «@» (18/10/2026): `testo` è quello mostrato («@Nome», come lo risolverà il server),
   * `menzioni` le parti da evidenziare, `testoInvio` il testo con i segnaposto da (ri)mandare.
   */
  menzioni?: ChatMenzione[];
  testoInvio?: string;
  /** `false`: rifiuto definitivo del server (es. menzione non valida), niente «Riprova». */
  riprovabile?: boolean;
  /** Sticker in invio (25/10/2026): `testo` vuoto, si manda solo `stickerId`. */
  sticker?: StickerMessaggio | null;
}

export type VoceMessaggio = {
  tipo: 'messaggio';
  chiave: string;
  messaggio: ChatMessaggio | MessaggioLocale;
  primoDellaSerie: boolean;
  ultimoDellaSerie: boolean;
};

/** Foto consecutive (almeno 2) con lo stesso `album`, in un'unica bolla a griglia. */
export type VoceAlbum = {
  tipo: 'album';
  chiave: string;
  album: string;
  messaggi: Array<ChatMessaggio | MessaggioLocale>;
  primoDellaSerie: boolean;
  ultimoDellaSerie: boolean;
};

export type VoceLista =
  | { tipo: 'giorno'; chiave: string; etichetta: string }
  | { tipo: 'nonLetti'; chiave: string; quanti: number }
  | VoceMessaggio
  | VoceAlbum;

const SERIE_MS = 5 * 60 * 1000;

const tempo = (iso: string): number => {
  const t = Date.parse(iso);
  return isNaN(t) ? 0 : t;
};

export const eLocale = (m: ChatMessaggio | MessaggioLocale): m is MessaggioLocale =>
  (m as ChatMessaggio).id === undefined;

/** Stesso contenuto (gli oggetti del server sono ricostruiti a ogni lettura: si confrontano i campi). */
const stessoMessaggio = (a: ChatMessaggio, b: ChatMessaggio): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * Campi che il nuovo non porta e l'attuale sì (18/10/2026):
 * - `importante` (stella privata): gli eventi socket non lo portano, si conserva il proprio valore;
 *   un messaggio eliminato o rimosso non è più importante (il server toglie la stella);
 * - con `conservaAnteprima` (conferma di un invio, arrivo dal socket): l'anteprima del link già
 *   arrivata con `messaggio_aggiornato` non si perde per una risposta precedente senza anteprima.
 */
const conCampiConservati = (attuale: ChatMessaggio, nuovo: ChatMessaggio, conservaAnteprima: boolean): ChatMessaggio => {
  let m = nuovo;
  if (nuovo.eliminato || nuovo.rimosso) {
    if (nuovo.importante === undefined && attuale.importante !== undefined) m = { ...m, importante: false };
  } else if (nuovo.importante === undefined && typeof attuale.importante === 'boolean') {
    m = { ...m, importante: attuale.importante };
  }
  if (conservaAnteprima && !nuovo.anteprimaLink && attuale.anteprimaLink && nuovo.testo === attuale.testo && !nuovo.eliminato && !nuovo.rimosso) {
    m = { ...m, anteprimaLink: attuale.anteprimaLink };
  }
  return m;
};

/**
 * Unione per `id` (il nuovo sostituisce l'attuale), ordinata per `creatoIl` poi `id`.
 * Un messaggio nuovo identico a quello attuale non lo sostituisce (stesso oggetto, così le bolle
 * in `React.memo` non si ridisegnano); se nulla cambia restituisce `attuali` stesso, e chi fa
 * `setMessaggi(prima => fondiMessaggi(prima, pagina))` non provoca alcun ridisegno.
 */
export const fondiMessaggi = (
  attuali: ChatMessaggio[],
  nuovi: ChatMessaggio[],
  opzioni: { conservaAnteprima?: boolean } = {}
): ChatMessaggio[] => {
  const perId: Record<string, ChatMessaggio> = {};
  attuali.forEach((m) => { perId[m.id] = m; });
  let cambiato = false;
  nuovi.forEach((nuovo) => {
    const attuale = perId[nuovo.id];
    const m = attuale ? conCampiConservati(attuale, nuovo, !!opzioni.conservaAnteprima) : nuovo;
    if (attuale && stessoMessaggio(attuale, m)) return;
    perId[m.id] = m;
    cambiato = true;
  });
  if (!cambiato) return attuali;
  return Object.keys(perId)
    .map((k) => perId[k])
    .sort((a, b) => {
      const d = tempo(a.creatoIl) - tempo(b.creatoIl);
      if (d !== 0) return d;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
};

const chiaveGiorno = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const chiaveMittente = (m: ChatMessaggio | MessaggioLocale): string => {
  if (eLocale(m) || m.mio) return 'io';
  if (m.tipo === 'sistema') return `sistema:${m.id}`;
  return `${m.mittente.tipo}:${m.mittente.associatoId || ''}`;
};

const stessaSerie = (a: ChatMessaggio | MessaggioLocale, b: ChatMessaggio | MessaggioLocale): boolean =>
  chiaveMittente(a) === chiaveMittente(b) &&
  chiaveGiorno(a.creatoIl) === chiaveGiorno(b.creatoIl) &&
  Math.abs(tempo(b.creatoIl) - tempo(a.creatoIl)) <= SERIE_MS;

/**
 * Messaggio che conta tra i non letti (stesse regole del backend): non mio,
 * non di sistema, non eliminato, non rimosso dalla moderazione.
 */
export const contaComeNonLetto = (m: ChatMessaggio | MessaggioLocale): m is ChatMessaggio =>
  !eLocale(m) && !m.mio && m.tipo !== 'sistema' && !m.eliminato && !m.rimosso;

/**
 * Ordine di visualizzazione: `creatoIl`, poi `id`; a parità di ora i messaggi
 * locali (senza id) vanno dopo quelli del server. Ordinamento stabile.
 */
const ordinaPerVisualizzazione = (messaggi: Array<ChatMessaggio | MessaggioLocale>) =>
  messaggi.slice().sort((a, b) => {
    const d = tempo(a.creatoIl) - tempo(b.creatoIl);
    if (d !== 0) return d;
    const la = eLocale(a);
    const lb = eLocale(b);
    if (la || lb) return la === lb ? 0 : la ? 1 : -1;
    const ia = (a as ChatMessaggio).id;
    const ib = (b as ChatMessaggio).id;
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });

/** Album di appartenenza (server o locale), oppure null. */
export const albumDi = (m: ChatMessaggio | MessaggioLocale): string | null => (m.album ? String(m.album) : null);

/** Foto viva: allegato immagine presente, non eliminata né rimossa (mai uno sticker, che ha l'allegato di compatibilità). */
export const fotoViva = (m: ChatMessaggio | MessaggioLocale): boolean => {
  if (!m.allegato || !/^image\//.test(m.allegato.mime) || eSticker(m)) return false;
  if (eLocale(m)) return true;
  return !m.eliminato && !m.rimosso;
};

export type DisposizioneAlbum = { schema: 'due' | 'tre' | 'griglia'; visibili: number; nascoste: number };

/** 2 foto affiancate, 3 = una grande + due, 4 o più = 2×2 con «+N» sull'ultima. */
export const disposizioneAlbum = (n: number): DisposizioneAlbum => {
  if (n <= 2) return { schema: 'due', visibili: 2, nascoste: 0 };
  if (n === 3) return { schema: 'tre', visibili: 3, nascoste: 0 };
  return { schema: 'griglia', visibili: 4, nascoste: n - 4 };
};

const posizioneAlbum = (m: ChatMessaggio | MessaggioLocale): number =>
  typeof m.albumIndice === 'number' ? m.albumIndice : Number.MAX_SAFE_INTEGER;

/**
 * Fonde in una voce «album» le foto vive consecutive con lo stesso `album`
 * (almeno 2). Le foto eliminate o rimosse dello stesso album non spezzano il
 * gruppo: il loro segnaposto resta, subito dopo la griglia. Una foto rimasta
 * sola torna singola. Le voci non toccate restano gli stessi oggetti.
 */
const raggruppaAlbum = (voci: VoceLista[]): VoceLista[] => {
  const uscita: VoceLista[] = [];
  let i = 0;
  while (i < voci.length) {
    const v = voci[i];
    const album = v.tipo === 'messaggio' ? albumDi(v.messaggio) : null;
    if (!album || v.tipo !== 'messaggio') {
      uscita.push(v);
      i += 1;
      continue;
    }
    // corsa di voci dello stesso album
    let j = i;
    while (j < voci.length && voci[j].tipo === 'messaggio' && albumDi((voci[j] as VoceMessaggio).messaggio) === album) j += 1;
    const corsa = voci.slice(i, j) as VoceMessaggio[];
    // nella griglia conta la posizione nell'album (server e locali possono avere orologi diversi)
    const vive = corsa.filter((x) => fotoViva(x.messaggio)).sort((a, b) => posizioneAlbum(a.messaggio) - posizioneAlbum(b.messaggio));
    const segnaposti = corsa.filter((x) => !fotoViva(x.messaggio));
    if (vive.length >= 2) {
      uscita.push({
        tipo: 'album',
        chiave: `album-${album}-${vive[0].chiave}`,
        album,
        messaggi: vive.map((x) => x.messaggio),
        primoDellaSerie: corsa[0].primoDellaSerie,
        ultimoDellaSerie: corsa[corsa.length - 1].ultimoDellaSerie,
      });
      segnaposti.forEach((x) => uscita.push(x));
    } else {
      corsa.forEach((x) => uscita.push(x));
    }
    i = j;
  }
  return uscita;
};

const stessiMessaggi = (a: Array<ChatMessaggio | MessaggioLocale>, b: Array<ChatMessaggio | MessaggioLocale>): boolean =>
  a.length === b.length && a.every((m, i) => m === b[i]);

/**
 * Voci da disegnare: separatori di giorno, banner «N messaggi non letti» prima
 * del messaggio `nonLettiDa`, serie = stesso mittente entro 5 minuti nello stesso giorno.
 *
 * `opzioni.precedenti`: le voci del giro precedente. Una voce invariata (stesso
 * messaggio, stessa posizione nella serie, stessa etichetta o conteggio) viene
 * riusata tale e quale, così `React.memo` sulle bolle non ridisegna ciò che non cambia.
 * `opzioni.album`: le foto consecutive con lo stesso `album` diventano una voce «album».
 */
export const costruisciVoci = (
  ingresso: Array<ChatMessaggio | MessaggioLocale>,
  opzioni: { nonLettiDa?: string | null; ora?: Date; precedenti?: VoceLista[]; album?: boolean } = {}
): VoceLista[] => {
  const ora = opzioni.ora || new Date();
  const voci: VoceLista[] = [];
  let giornoPrecedente: string | null = null;
  // ordinato per sicurezza: con un elenco disordinato le chiavi dei giorni si ripeterebbero
  const messaggi = ordinaPerVisualizzazione(ingresso);
  const vecchie = new Map<string, VoceLista>();
  (opzioni.precedenti || []).forEach((v) => vecchie.set(v.chiave, v));

  /** La voce precedente con la stessa chiave se identica, altrimenti la nuova. */
  const riusa = (nuova: VoceLista): VoceLista => {
    const vecchia = vecchie.get(nuova.chiave);
    if (!vecchia || vecchia.tipo !== nuova.tipo) return nuova;
    if (vecchia.tipo === 'giorno' && nuova.tipo === 'giorno') return vecchia.etichetta === nuova.etichetta ? vecchia : nuova;
    if (vecchia.tipo === 'nonLetti' && nuova.tipo === 'nonLetti') return vecchia.quanti === nuova.quanti ? vecchia : nuova;
    if (vecchia.tipo === 'messaggio' && nuova.tipo === 'messaggio') {
      return vecchia.messaggio === nuova.messaggio &&
        vecchia.primoDellaSerie === nuova.primoDellaSerie &&
        vecchia.ultimoDellaSerie === nuova.ultimoDellaSerie
        ? vecchia
        : nuova;
    }
    if (vecchia.tipo === 'album' && nuova.tipo === 'album') {
      return stessiMessaggi(vecchia.messaggi, nuova.messaggi) &&
        vecchia.primoDellaSerie === nuova.primoDellaSerie &&
        vecchia.ultimoDellaSerie === nuova.ultimoDellaSerie
        ? vecchia
        : nuova;
    }
    return nuova;
  };

  messaggi.forEach((m, i) => {
    const giorno = chiaveGiorno(m.creatoIl);
    if (giorno !== giornoPrecedente) {
      voci.push(riusa({ tipo: 'giorno', chiave: `giorno-${giorno}`, etichetta: etichettaGiorno(m.creatoIl, ora) }));
      giornoPrecedente = giorno;
    }
    if (opzioni.nonLettiDa && !eLocale(m) && m.id === opzioni.nonLettiDa) {
      const quanti = messaggi.slice(i).filter(contaComeNonLetto).length;
      if (quanti > 0) voci.push(riusa({ tipo: 'nonLetti', chiave: 'non-letti', quanti }));
    }
    const prima = i > 0 ? messaggi[i - 1] : null;
    const dopo = i < messaggi.length - 1 ? messaggi[i + 1] : null;
    voci.push(riusa({
      tipo: 'messaggio',
      chiave: eLocale(m) ? `locale-${m.clientId}` : m.id,
      messaggio: m,
      primoDellaSerie: !prima || !stessaSerie(prima, m),
      ultimoDellaSerie: !dopo || !stessaSerie(m, dopo),
    }));
  });

  return opzioni.album ? raggruppaAlbum(voci).map(riusa) : voci;
};

/** Spunte di un mio messaggio (solo dirette: nei canali `ricevute` è null). */
export const statoSpunte = (m: ChatMessaggio, ricevute: ChatRicevute | null): StatoRicevuta | null => {
  if (!m.mio || !ricevute) return null;
  const creato = tempo(m.creatoIl);
  if (ricevute.lettoFino && tempo(ricevute.lettoFino) >= creato) return 'letto';
  if (ricevute.consegnatoFino && tempo(ricevute.consegnatoFino) >= creato) return 'consegnato';
  return 'inviato';
};
